/**
 * ReadinessChip — the universal three-state badge used everywhere
 * (consumer nodes' top-right corners, the mapping canvas pill).
 *
 * Visual states:
 *
 *   ✅ Ready         — green, no count
 *   ⚠ N notes        — amber, count of advisory/info
 *   ⛔ N to fix       — red,   count of blockers
 *   … Checking       — neutral, in-flight
 *   — (em-dash)      — neutral, nothing to check yet (empty state)
 *
 * Single component used by every consumer surface so the user learns
 * one shape and one palette. Click-handler is wired up by the host;
 * the chip itself doesn't open panels.
 */
import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  CheckCircle as ReadyIcon,
  WarningAmber as AdvisoryIcon,
  Block as BlockerIcon,
  HourglassEmpty as CheckingIcon,
  RemoveCircleOutline as EmptyIcon,
} from '@mui/icons-material';

import type { ReadinessFinding, ReadinessStatus } from '../../../types/readiness';

export type ChipState = ReadinessStatus | 'checking' | 'empty';

interface ReadinessChipProps {
  state: ChipState;
  /** Count of findings (blockers when ⛔; advisories when ⚠). Hidden when ready/empty. */
  count?: number;
  /** Optional click handler; chip is interactive when present. */
  onClick?: () => void;
  /** Optional tooltip override; defaults to status copy. */
  tooltip?: string;
  /** Compact variant for tight spaces (node corners). */
  size?: 'small' | 'medium';
}

const STATE_CONFIG: Record<
  ChipState,
  {
    icon: React.ReactElement;
    label: (count?: number) => string;
    color:
      | 'default'
      | 'success'
      | 'warning'
      | 'error';
    defaultTooltip: string;
  }
> = {
  ready: {
    icon: <ReadyIcon fontSize="small" />,
    label: () => 'Ready',
    color: 'success',
    defaultTooltip: 'Ready to download / export.',
  },
  ready_with_notes: {
    icon: <AdvisoryIcon fontSize="small" />,
    label: (n) => (n ? `${n} notes` : 'Notes'),
    color: 'warning',
    defaultTooltip: 'Ready, with optional notes you should review.',
  },
  not_ready: {
    icon: <BlockerIcon fontSize="small" />,
    label: (n) => (n ? `${n} to fix` : 'Not ready'),
    color: 'error',
    defaultTooltip: 'Resolve the listed issues to enable download.',
  },
  checking: {
    icon: <CheckingIcon fontSize="small" />,
    label: () => 'Checking',
    color: 'default',
    defaultTooltip: 'Recomputing readiness…',
  },
  empty: {
    icon: <EmptyIcon fontSize="small" />,
    label: () => '—',
    color: 'default',
    defaultTooltip: 'Nothing to check yet.',
  },
};

export function ReadinessChip({
  state,
  count,
  onClick,
  tooltip,
  size = 'small',
}: ReadinessChipProps): React.ReactElement {
  const cfg = STATE_CONFIG[state];
  const chip = (
    <Chip
      icon={cfg.icon}
      label={cfg.label(count)}
      color={cfg.color}
      size={size}
      onClick={onClick}
      // Subtle visual cue that the chip is interactive when wired.
      clickable={!!onClick}
      sx={{
        fontWeight: state === 'not_ready' ? 600 : 500,
      }}
    />
  );
  return (
    <Tooltip title={tooltip ?? cfg.defaultTooltip} arrow>
      {chip}
    </Tooltip>
  );
}

/**
 * Helper to derive the chip state + count from a list of findings.
 * Mirrors the Python AssemblyResult.status() derivation. Hosts call
 * this when they have findings in hand; the dedicated ``checking`` /
 * ``empty`` states must be supplied explicitly.
 */
export function chipPropsFromFindings(findings: ReadinessFinding[]): {
  state: ChipState;
  count: number;
} {
  if (findings.length === 0) return { state: 'ready', count: 0 };
  const blockers = findings.filter((f) => f.severity === 'blocker').length;
  if (blockers > 0) return { state: 'not_ready', count: blockers };
  const advisories = findings.filter((f) => f.severity === 'advisory').length;
  if (advisories > 0) return { state: 'ready_with_notes', count: advisories };
  return { state: 'ready', count: 0 };
}

export default ReadinessChip;
